// Fabrique de PNJ : instancie dans une scène les personnages de data/characters.json
// dont le homeLocation correspond au lieu, en utilisant leur portrait DiceBear.
//
// Séparation des responsabilités :
//   - QUI vit où  -> data/characters.json (homeLocation)
//   - OÙ exactement sur la carte (coordonnées de tuiles) -> `placements`, fourni par la scène,
//     car ça dépend de la carte Tiled que tu dessines.
//
// Utilisation type dans une scène (voir aussi game/LACAZE_README.md) :
//
//   import { preloadLocationPortraits, spawnCharactersForLocation } from './components/CharacterFactory'
//
//   preload() {
//     super.preload(); super.loadAvatarSpritesheet(); super.loadMapImages()
//     preloadLocationPortraits(this, 'administration')   // file les textures DiceBear
//   }
//   createNpcs() {
//     spawnCharactersForLocation(this, {
//       locationId: 'administration',
//       placements: { 'principal-robin': { x: 6, y: 4, facing: Direction.DOWN } },
//       onInteract: (scene, c) => console.log(c.displayName, c.dialogueHook), // brancher rexUI ici plus tard
//     })
//   }
import { Direction } from 'grid-engine'
import GameScene from '../GameScene'
import { AvatarNpc } from './AvatarNpc'
import {
    CharacterData,
    getCharactersByLocation,
} from '../../data/characters'
import { portraitKey, preloadPortraits, PORTRAIT_SIZE } from '../../avatars/dicebear'
import GlobalInfo from '../../GlobalInfo'

/** Position (en tuiles) et orientation d'un personnage dans une scène donnée. */
export interface Placement {
    x: number
    y: number
    facing?: Direction
    /** échelle du sprite ; défaut = `scale` global de spawnCharactersForLocation */
    scale?: number
}

export interface SpawnOptions {
    /** id de lieu (cf. data/locations.json) */
    locationId: string
    /** position de chaque personnage par son id : { 'principal-robin': { x, y } } */
    placements: Record<string, Placement>
    /** échelle par défaut des portraits (96px de base -> ~0.5 pour des tuiles 16px) */
    scale?: number
    /** action déclenchée à l'interaction (touche E). Par défaut : log du dialogueHook. */
    onInteract?: (scene: GameScene, character: CharacterData) => void
}

/**
 * À APPELER DANS preload() : met en file les portraits DiceBear de tous les personnages du lieu.
 */
export function preloadLocationPortraits(
    scene: GameScene,
    locationId: string,
    size: number = PORTRAIT_SIZE
): void {
    preloadPortraits(scene, getCharactersByLocation(locationId), size)
}

/**
 * À APPELER DANS createNpcs() : crée un AvatarNpc pour chaque personnage du lieu qui a une
 * position dans `placements`. Retourne les PNJ créés.
 */
export function spawnCharactersForLocation(
    scene: GameScene,
    opts: SpawnOptions
): AvatarNpc[] {
    const { locationId, placements, scale = 0.3, onInteract } = opts
    const spawned: AvatarNpc[] = []
    // registre tuile -> personnage, pour ouvrir la fiche au TAP (cf. GameScene pointerdown).
    const byTile: Record<string, CharacterData> = ((scene as any).kaziByTile = {})

    getCharactersByLocation(locationId).forEach(character => {
        const place = placements[character.id]
        if (!place) return // pas de position fournie pour ce personnage dans cette scène

        const key = portraitKey(character.id)
        if (!scene.textures.exists(key)) {
            console.warn(
                `[CharacterFactory] texture "${key}" absente. ` +
                `Appelle preloadLocationPortraits(scene, "${locationId}") dans preload().`
            )
            return
        }

        const npc = new AvatarNpc({
            scene,
            xPosition: place.x,
            yPosition: place.y,
            texture: key,
            scale: place.scale ?? scale,
            facingDirection: place.facing ?? Direction.DOWN,
            action: () =>
                onInteract ? onInteract(scene, character) : showKazi(character),
        })
        byTile[`${place.x},${place.y}`] = character
        spawned.push(npc)
    })

    return spawned
}

// Ouvre la FICHE Kazi (portrait + nom + rôle + réplique + jauges d'humeur) via l'overlay.
// Découplé de l'UI via le bus global GlobalInfo. Appelé par E (interaction) ET le tap (GameScene).
export function showKazi(character: CharacterData): void {
    GlobalInfo.emit('show-kazi', {
        id: character.id,
        displayName: character.displayName,
        role: character.role,
        dialogueHook: character.dialogueHook,
    })
}
